import React, { useState, useEffect, useMemo } from 'react'
import PipelineFlowChart from '../../components/charts/PipelineFlowChart'

interface PipelineStage {
  id: string
  name: string
  status: 'running' | 'completed' | 'failed' | 'pending'
  progress: number
  records_processed: number
  last_run: string
}

const DataPipeline: React.FC = () => {
  const [stages, setStages] = useState<PipelineStage[]>([])

  const mockStages = useMemo(() => [
    {
      id: 'ingestion',
      name: 'Data Ingestion',
      status: 'running' as const,
      progress: 75,
      records_processed: 15420,
      last_run: new Date().toISOString()
    },
    {
      id: 'validation',
      name: 'Data Validation',
      status: 'completed' as const,
      progress: 100,
      records_processed: 15420,
      last_run: new Date(Date.now() - 300000).toISOString()
    },
    {
      id: 'transformation',
      name: 'Data Transformation',
      status: 'pending' as const,
      progress: 0,
      records_processed: 0,
      last_run: new Date(Date.now() - 3600000).toISOString()
    },
    {
      id: 'aggregation',
      name: 'Analytics Aggregation',
      status: 'failed' as const,
      progress: 45,
      records_processed: 8230,
      last_run: new Date(Date.now() - 1800000).toISOString()
    }
  ], []);

  useEffect(() => {
    setStages(mockStages)
  }, [mockStages])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'text-blue-600 bg-blue-100'
      case 'completed': return 'text-green-600 bg-green-100'
      case 'failed': return 'text-red-600 bg-red-100'
      case 'pending': return 'text-yellow-600 bg-yellow-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Data Pipeline</h1>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Trigger Pipeline
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Records</h3>
          <p className="text-2xl font-bold text-blue-600">1.2M</p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Processing Rate</h3>
          <p className="text-2xl font-bold text-green-600">850/sec</p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Success Rate</h3>
          <p className="text-2xl font-bold text-purple-600">98.5%</p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Latency</h3>
          <p className="text-2xl font-bold text-orange-600">125ms</p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Pipeline Stages</h3>
        <div className="space-y-4">
          {stages.map((stage, index) => (
            <div key={stage.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-gray-900 dark:text-white">{stage.name}</h4>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(stage.status)}`}>
                  {stage.status}
                </span>
              </div>
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                <span>Records: {stage.records_processed.toLocaleString()}</span>
                <span>Last run: {new Date(stage.last_run).toLocaleTimeString()}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    stage.status === 'failed' ? 'bg-red-500' :
                    stage.status === 'completed' ? 'bg-green-500' :
                    stage.status === 'running' ? 'bg-blue-500' : 'bg-gray-400'
                  }`}
                  style={{ width: `${stage.progress}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
        <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
          Pipeline Flow 🔄
        </h3>
        <PipelineFlowChart />
      </div>
    </div>
  )
}

export default DataPipeline